import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";

const CATS = [
  { key: "leave", label: "Leave", desc: "Submissions, approvals and rejections" },
  { key: "timesheet", label: "Timesheet", desc: "Weekly submissions, approvals and rejections" },
  { key: "project", label: "Projects", desc: "When you're assigned to or removed from a project" },
  { key: "user", label: "Account & Admin", desc: "New users and administrative events" },
];

export default function NotificationPreferences() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/notifications/preferences").then((r) => setPrefs(r.data)).catch((e) => toast.error(formatErr(e)));
  }, []);

  const toggle = (cat, channel) => {
    setPrefs({ ...prefs, [cat]: { ...prefs[cat], [channel]: !prefs[cat][channel] } });
  };

  const setQuiet = (k, v) => {
    setPrefs({ ...prefs, quiet: { ...(prefs.quiet || {}), [k]: v } });
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/notifications/preferences", prefs); toast.success("Preferences saved"); }
    catch (e) { toast.error(formatErr(e)); }
    setSaving(false);
  };

  if (!prefs) return <div className="text-neutral-500">Loading…</div>;

  return (
    <div data-testid="notification-preferences-page">
      <PageHeader overline="Settings" title="Notification Preferences"
        subtitle="Choose how you want to be notified for each type of event" />

      <div className="bg-white border border-[#E5E5E5] max-w-2xl">
        <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-5 py-3 border-b border-[#E5E5E5] bg-[#F7F7F5] text-xs font-semibold uppercase tracking-wider text-neutral-500">
          <div>Event</div><div className="w-20 text-center">In-app</div><div className="w-20 text-center">Email</div>
        </div>
        {CATS.map((c) => (
          <div key={c.key} className="grid grid-cols-[1fr_auto_auto] gap-4 px-5 py-4 border-b border-[#F0F0EE] items-center" data-testid={`pref-row-${c.key}`}>
            <div>
              <div className="font-medium text-sm">{c.label}</div>
              <div className="text-xs text-neutral-500">{c.desc}</div>
            </div>
            <div className="w-20 text-center">
              <Switch on={prefs[c.key]?.in_app} onClick={() => toggle(c.key, "in_app")} testid={`${c.key}-in_app`} />
            </div>
            <div className="w-20 text-center">
              <Switch on={prefs[c.key]?.email} onClick={() => toggle(c.key, "email")} testid={`${c.key}-email`} />
            </div>
          </div>
        ))}
        <div className="px-5 py-4">
          <Button onClick={save} disabled={saving} className="rounded-none bg-[#FF5500] hover:bg-[#E64D00] text-white" data-testid="save-prefs">
            {saving ? "Saving…" : "Save Preferences"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] max-w-2xl mt-6" data-testid="quiet-hours-card">
        <div className="px-5 py-3 border-b border-[#E5E5E5] bg-[#F7F7F5] flex items-center justify-between">
          <div>
            <div className="font-head font-semibold text-sm">Quiet Hours</div>
            <div className="text-xs text-neutral-500">Mute non-urgent emails in the evening and on weekends</div>
          </div>
          <Switch on={prefs.quiet?.enabled} onClick={() => setQuiet("enabled", !prefs.quiet?.enabled)} testid="quiet-enabled" />
        </div>
        <div className={`px-5 py-4 space-y-4 ${prefs.quiet?.enabled ? "" : "opacity-50 pointer-events-none"}`}>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <div className="eyebrow mb-1">From</div>
              <input type="time" value={prefs.quiet?.start || "19:00"} onChange={(e) => setQuiet("start", e.target.value)}
                className="h-10 border border-[#E5E5E5] px-2 bg-white text-sm" data-testid="quiet-start" />
            </div>
            <div>
              <div className="eyebrow mb-1">Until</div>
              <input type="time" value={prefs.quiet?.end || "08:00"} onChange={(e) => setQuiet("end", e.target.value)}
                className="h-10 border border-[#E5E5E5] px-2 bg-white text-sm" data-testid="quiet-end" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!prefs.quiet?.mute_weekends} onChange={(e) => setQuiet("mute_weekends", e.target.checked)}
              className="accent-[#FF5500]" data-testid="quiet-weekends" />
            Also mute all day on Saturdays and Sundays
          </label>
          <div className="text-xs text-neutral-500 border-l-2 border-[#FF5500] pl-3">
            During quiet hours we won't email you about routine updates — they still appear in your notification bell.
            Urgent items (leave/timesheet rejections, new-user alerts) always come through.
          </div>
        </div>
        <div className="px-5 py-4 border-t border-[#F0F0EE]">
          <Button onClick={save} disabled={saving} className="rounded-none bg-[#FF5500] hover:bg-[#E64D00] text-white" data-testid="save-quiet">
            {saving ? "Saving…" : "Save Quiet Hours"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Switch({ on, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={`toggle-${testid}`}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${on ? "bg-[#FF5500]" : "bg-neutral-300"}`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}
